import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

// Check if a username looks auto-generated from email (e.g. "web3enterprises")
// We detect this by checking if it matches the email prefix pattern
function looksAutoGenerated(username, email) {
  if (!username || !email) return false;
  const emailPrefix = email.split('@')[0].toLowerCase().replace(/[^a-z0-9_]/g, '_');
  return username.toLowerCase() === emailPrefix ||
    username.toLowerCase().startsWith(emailPrefix) ||
    /^\w+\d+$/.test(username); // ends in number (collision suffix)
}

function generateUsername(displayName, email) {
  // Try display name first (e.g. "John Smith" → "johnsmith")
  if (displayName) {
    return displayName.toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9_]/g, '')
      .slice(0, 20);
  }
  // Fall back to email prefix
  return email.split('@')[0].toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 20);
}

export default function UsernameSetupPage() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername]     = useState('');
  const [checking, setChecking]     = useState(false);
  const [available, setAvailable]   = useState(null); // null | true | false
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  // Pre-fill with a suggestion from Google display name
  useEffect(() => {
    if (!user) return;
    const suggested = generateUsername(
      user.user_metadata?.full_name || user.user_metadata?.name,
      user.email
    );
    setUsername(suggested);
  }, [user?.id]);

  // Check availability with debounce
  useEffect(() => {
    if (!username || username.length < 3) { setAvailable(null); return; }
    if (!/^[a-z0-9_]+$/.test(username)) { setAvailable(false); return; }

    setChecking(true);
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .neq('id', user?.id)  // exclude self
        .single();
      setAvailable(!data);
      setChecking(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [username]);

  const handleSave = async () => {
    if (!available || saving) return;
    setSaving(true);
    try {
      const { error: err } = await supabase
        .from('profiles')
        .update({ username: username.trim() })
        .eq('id', user.id);
      if (err) throw err;
      await refreshProfile();
      navigate('/app');
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const handleSkip = async () => {
    // Skip — they'll keep the auto-generated username for now
    navigate('/app');
  };

  const isValid = username.length >= 3 && /^[a-z0-9_]+$/.test(username);

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>
          CopyPaste<span style={{ color: '#7f77dd' }}>Genius</span>
        </div>

        <div style={{ fontSize: 32, marginBottom: 8 }}>👋</div>
        <div style={s.title}>One last thing</div>
        <div style={s.sub}>
          Choose a username — this is how your prompts will appear to others.
        </div>

        <div style={s.field}>
          <label style={s.label}>Username</label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
              fontSize: 13, color: 'var(--dim)', pointerEvents: 'none',
            }}>@</span>
            <input
              style={{
                ...s.input, paddingLeft: 26,
                borderColor: username && !isValid ? 'rgba(226,75,74,0.6)'
                  : available === true ? 'rgba(29,158,117,0.5)'
                  : available === false ? 'rgba(226,75,74,0.6)'
                  : undefined,
              }}
              placeholder="yourhandle"
              value={username}
              onChange={e => {
                setAvailable(null);
                setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
              }}
              autoFocus
              maxLength={30}
            />
            <span style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              fontSize: 12,
              color: available === true ? '#5dcaa5' : available === false ? 'var(--danger)' : 'var(--dim)',
            }}>
              {checking ? '…' : available === true ? '✓' : available === false ? '✗' : ''}
            </span>
          </div>

          {/* Feedback messages */}
          {username && username.length < 3 && (
            <div style={s.hint}>At least 3 characters required</div>
          )}
          {username && !isValid && username.length >= 3 && (
            <div style={{ ...s.hint, color: 'var(--danger)' }}>Only letters, numbers, and underscores</div>
          )}
          {available === false && isValid && (
            <div style={{ ...s.hint, color: 'var(--danger)' }}>That username is taken</div>
          )}
          {available === true && (
            <div style={{ ...s.hint, color: '#5dcaa5' }}>Looks good!</div>
          )}
        </div>

        {error && <div style={{ fontSize: 12, color: 'var(--danger)', textAlign: 'center' }}>{error}</div>}

        <button
          onClick={handleSave}
          disabled={!available || !isValid || saving}
          style={{
            ...s.primaryBtn,
            opacity: (!available || !isValid || saving) ? 0.5 : 1,
            cursor: (!available || !isValid || saving) ? 'not-allowed' : 'pointer',
          }}>
          {saving ? 'Saving...' : 'Set username →'}
        </button>

        <button onClick={handleSkip} style={s.skipBtn}>
          Skip for now — I'll set it later
        </button>

        <div style={{ fontSize: 11, color: 'var(--dim)', textAlign: 'center', lineHeight: 1.6 }}>
          You can change your username anytime in Settings.
        </div>
      </div>
    </div>
  );
}

const s = {
  page:       { minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'var(--font)' },
  card:       { background: 'var(--surface)', border: '0.5px solid var(--border2)', borderRadius: 16, padding: '36px 32px', maxWidth: 400, width: '100%', display: 'flex', flexDirection: 'column', gap: 14 },
  logo:       { fontSize: 17, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.03em' },
  title:      { fontSize: 20, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em' },
  sub:        { fontSize: 13, color: 'var(--muted)', lineHeight: 1.65, marginTop: -6 },
  field:      { display: 'flex', flexDirection: 'column', gap: 5 },
  label:      { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', fontWeight: 500 },
  input:      { background: 'var(--bg)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none', width: '100%', transition: 'border-color 0.12s' },
  hint:       { fontSize: 11, color: 'var(--dim)' },
  primaryBtn: { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', padding: '11px', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer', width: '100%', transition: 'opacity 0.15s' },
  skipBtn:    { background: 'none', border: 'none', color: 'var(--dim)', fontFamily: 'var(--font)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', padding: 0, textAlign: 'center' },
};
